import { useEffect, useState } from "react";
import { User, api } from "../api";
import { useAuth } from "../auth";
import { UserGrantsModal } from "../components/UserGrantsModal";
import { NewUserModal } from "../components/NewUserModal";
import { SetPasswordModal } from "../components/SetPasswordModal";
import { DeleteUserModal } from "../components/DeleteUserModal";
import { RenameUserModal } from "../components/RenameUserModal";

const BREAK_GLASS_USERNAME = "admin";

function isProtected(u: User): boolean {
  return u.username === BREAK_GLASS_USERNAME;
}

function AdminBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
      title="Administrator"
    >
      <span aria-hidden>★</span>admin
    </span>
  );
}

function ProtectedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
      title="Break-glass account; cannot be demoted, disabled, renamed, or deleted"
    >
      <span aria-hidden>🛡</span>break-glass
    </span>
  );
}

export function UsersPage() {
  const { token, me } = useAuth();
  const [users, setUsers] = useState<User[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [grantUser, setGrantUser] = useState<User | null>(null);
  const [pwUser, setPwUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [renameUser, setRenameUser] = useState<User | null>(null);
  const [showNew, setShowNew] = useState(false);

  const reload = () => {
    if (!token) return;
    api
      .listUsers(token)
      .then(setUsers)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  const onToggleAdmin = async (u: User) => {
    if (!token) return;
    if (isProtected(u)) {
      alert(`Refusing to change admin status on the break-glass user '${u.username}'.`);
      return;
    }
    if (u.id === me?.id && u.is_admin) {
      alert("Refusing to demote yourself. Use another admin to do this.");
      return;
    }
    try {
      await api.patchUser(token, u.id, { is_admin: !u.is_admin });
      reload();
    } catch (e) {
      alert(`Update failed: ${(e as { message?: string })?.message ?? e}`);
    }
  };

  const onToggleDisabled = async (u: User) => {
    if (!token) return;
    if (isProtected(u)) {
      alert(`Refusing to disable the break-glass user '${u.username}'.`);
      return;
    }
    try {
      await api.patchUser(token, u.id, { disabled: !u.disabled });
      reload();
    } catch (e) {
      alert(`Update failed: ${(e as { message?: string })?.message ?? e}`);
    }
  };

  return (
    <div className="space-y-4">
      {token && grantUser && (
        <UserGrantsModal
          token={token}
          user={grantUser}
          onClose={() => setGrantUser(null)}
          onSaved={() => reload()}
        />
      )}
      {token && showNew && (
        <NewUserModal
          token={token}
          onClose={() => setShowNew(false)}
          onCreated={() => reload()}
        />
      )}
      {token && pwUser && (
        <SetPasswordModal
          token={token}
          user={pwUser}
          onClose={() => setPwUser(null)}
        />
      )}
      {token && deleteUser && (
        <DeleteUserModal
          token={token}
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onDeleted={() => reload()}
        />
      )}
      {token && renameUser && (
        <RenameUserModal
          token={token}
          user={renameUser}
          onClose={() => setRenameUser(null)}
          onSaved={() => reload()}
        />
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Account management</h2>
        <button onClick={() => setShowNew(true)} className="btn-primary" type="button">
          + New user
        </button>
      </div>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}
      <div className="card overflow-hidden">
        {users === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th>id</th>
                <th>username</th>
                <th>role</th>
                <th>status</th>
                <th className="text-right">actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const protectedRow = isProtected(u);
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-t border-slate-100 align-middle">
                    <td className="font-mono">{u.id}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className={u.is_admin ? "font-semibold text-slate-900" : ""}>{u.username}</span>
                        {protectedRow && <ProtectedBadge />}
                        <button
                          onClick={() => setRenameUser(u)}
                          className="btn text-xs py-0.5 px-2"
                          type="button"
                          disabled={protectedRow}
                          title={protectedRow ? "Break-glass user cannot be renamed" : "Rename"}
                        >
                          rename
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {u.is_admin ? (
                          <AdminBadge />
                        ) : (
                          <span className="text-xs text-slate-500">analyst</span>
                        )}
                        <button
                          onClick={() => void onToggleAdmin(u)}
                          className="btn text-xs py-1"
                          type="button"
                          disabled={protectedRow || (isSelf && u.is_admin)}
                          title={
                            protectedRow
                              ? "Break-glass user is always admin"
                              : isSelf && u.is_admin
                                ? "Cannot demote yourself"
                                : u.is_admin
                                  ? "Demote to analyst"
                                  : "Promote to admin"
                          }
                        >
                          {u.is_admin ? "demote" : "promote"}
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {u.disabled ? (
                          <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 text-[11px] uppercase tracking-wide">
                            disabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 text-[11px] uppercase tracking-wide">
                            active
                          </span>
                        )}
                        <button
                          onClick={() => void onToggleDisabled(u)}
                          className="btn text-xs py-1"
                          type="button"
                          disabled={protectedRow}
                          title={
                            protectedRow ? "Break-glass user cannot be disabled" : u.disabled ? "Re-enable" : "Disable"
                          }
                        >
                          {u.disabled ? "enable" : "disable"}
                        </button>
                      </div>
                    </td>
                    <td className="text-right space-x-2 whitespace-nowrap">
                      <button type="button" onClick={() => setGrantUser(u)} className="btn-primary text-xs py-1">
                        schema grants
                      </button>
                      <button type="button" onClick={() => setPwUser(u)} className="btn text-xs py-1">
                        set password
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteUser(u)}
                        className="btn-danger text-xs py-1"
                        disabled={protectedRow || isSelf}
                        title={
                          protectedRow
                            ? "Break-glass user cannot be deleted"
                            : isSelf
                              ? "Cannot delete yourself"
                              : "Delete user"
                        }
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Per-user MCP schema access uses <strong>schema grants</strong> (PUT replaces the full grant set). The
        break-glass user (<code>{BREAK_GLASS_USERNAME}</code>) cannot be demoted, disabled, renamed, or deleted —
        rotate its password instead. Demoting / disabling / deleting the last active admin is also refused server-side.
      </p>
    </div>
  );
}
